import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { BookOpen, Plus, Library, LogOut, User, LayoutDashboard, Users, Send, ClipboardCheck, CreditCard, Shield, GraduationCap, Printer } from 'lucide-react';

export const Navbar = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/create', label: 'Create Quiz', icon: Plus },
    { path: '/practice-sheets', label: 'Practice', icon: Printer },
    { path: '/library', label: 'Library', icon: Library },
    { path: '/students', label: 'Students', icon: Users },
    { path: '/assignments', label: 'Assign', icon: Send },
    { path: '/grading', label: 'Grading', icon: ClipboardCheck },
    { path: '/grades', label: 'Grades', icon: GraduationCap },
  ];

  // Add admin link if user is admin
  if (isAdmin) {
    navItems.unshift({ path: '/admin', label: 'Admin', icon: Shield });
  }

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
              <BookOpen className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-serif text-xl font-semibold text-primary">HomeClass</span>
          </Link>

          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-1">
                {navItems.map((item) => (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant={isActive(item.path) ? 'secondary' : 'ghost'}
                      size="sm"
                      className={`gap-2 ${item.path === '/admin' ? 'text-primary' : ''}`}
                      data-testid={`nav-${item.path.slice(1)}`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Button>
                  </Link>
                ))}
              </div>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-trigger">
                    <div className={`w-8 h-8 ${isAdmin ? 'bg-primary' : 'bg-primary/10'} rounded-full flex items-center justify-center`}>
                      {isAdmin ? (
                        <Shield className="w-4 h-4 text-primary-foreground" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{user?.name}</p>
                      {isAdmin && (
                        <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded">Admin</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  
                  {/* Mobile Navigation */}
                  <div className="lg:hidden">
                    {navItems.map((item) => (
                      <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}>
                        <item.icon className="w-4 h-4 mr-2" />
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </div>
                  
                  <DropdownMenuItem onClick={() => navigate('/pricing')} data-testid="pricing-link">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/student/login">
                <Button variant="ghost" size="sm" data-testid="nav-student-login">
                  Student Login
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="nav-login">
                  Teacher Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="nav-signup">
                  Get Started
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
